import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import getAnswersQuery from '../../pages/Dashboard/queries/getAnswerData.graphql';
import getAnswersSupervisorQuery from '../../pages/Dashboard/queries/getAnswerDataSupervisor.graphql';
import getAdministeredPlansQuery from '../../pages/Administer/queries/getAdministeredPlans.graphql';
import getAdministeredPlansSupervisorQuery from '../../pages/Administer/queries/getAdministeredPlansSupervisor.graphql';

import { client } from 'cccisd-apollo';
import _get from 'lodash/get';
import _setWith from 'lodash/setWith';
import _some from 'lodash/some';

import { isDateInPast } from '../../pages/Administer/helpers';

import ORG_CUSTOM_PLAN_ASSIGNMENT_HANDLES from '../../pages/Administer/ORG_CUSTOM_PLAN_ASSIGNMENT_HANDLES.json';

const USER = window.cccisd && window.cccisd.fortress.user.acting;
const isSupervisor = USER?.role?.handle === 'supervisor';
const isCareCoordinator = USER?.role?.handle === 'careCoordinator';

const initialState = {
    allAnswerData: null,
    selectedYouth: null,
    selectedCareCoordinator: isCareCoordinator ? USER.id : null,
    aggregateScoreAverages: null,
    filters: {
        startDate: '',
        endDate: '',
        planName: '',
    },
    administeredPlansData: {},
    youthAndCaregiverSessions: [],
};

// Adds one answer's value to the running sum/count at the given path,
// and adds the pawn, role, and responseDate
const accumulateAnswer = (scoreAverages, path, answerValue, pawn, role, responseDate) => {
    const sum = _get(scoreAverages, `${path}.sum`, 0);
    const count = _get(scoreAverages, `${path}.numberOfQuestionsAnswered`, 0);
    _setWith(scoreAverages, `${path}.sum`, sum + answerValue, Object);
    _setWith(scoreAverages, `${path}.numberOfQuestionsAnswered`, count + 1, Object);
    _setWith(scoreAverages, `${path}.pawn`, pawn, Object);
    _setWith(scoreAverages, `${path}.role`, role, Object);
    _setWith(scoreAverages, `${path}.responseDate`, responseDate ?? null, Object);
};

// Builds a nested scoreAverages object by planName > timepoint > domain > pawnId
// Adds date range and plan name filters. Called on filter change
export const computeScoreAverages = (allAnswerData, youthList, filters, selectedCareCoordinator, selectedYouth) => {
    const startDateFilter = filters.startDate || false;
    const endDateFilter = filters.endDate || false;
    const planNameFilter = filters.planName;
    // Youth answers match on pawnId; caregiver answers match on their parent youth field
    const selectedYouthFilter = selectedYouth ? selectedYouth.pawn.pawnId : false;

    const isWithinFilters = answer => {
        const responseDate = new Date(answer.responseDate);
        return (
            (!startDateFilter || startDateFilter <= responseDate) &&
            (!endDateFilter || endDateFilter >= responseDate) &&
            (!planNameFilter || planNameFilter.toLowerCase() === answer.planName.toLowerCase()) &&
            (!selectedYouthFilter ||
                answer.pawnId === selectedYouthFilter ||
                _some(selectedYouth.parentRoles.caregiverList, caregiver => caregiver.pawn.pawnId === answer.pawnId))
        );
    };

    const scoreAverages = {};

    Object.keys(allAnswerData).forEach(domain => {
        if (domain === 'feedback') return;

        Object.keys(allAnswerData[domain]).forEach(variableKey => {
            const variableData = allAnswerData[domain][variableKey];
            if (!variableData.answers) return;

            // only process selected youth in this average calculation
            // (or all if 'All' is selected in Aggregate Data tab)
            let listOfYouthToInclude = [];
            if (selectedYouth) {
                listOfYouthToInclude.push(selectedYouth);
            } else {
                listOfYouthToInclude.push(...youthList);
            }

            listOfYouthToInclude.forEach(currentYouth => {
                const youthAnswer = _get(variableData.answers, currentYouth.pawn.pawnId, false);
                const youthParentPlanDeploymentIds = _get(currentYouth, 'selectedAssignmentPlanList', []).map(
                    assignmentPlan => assignmentPlan.assignmentPlanId
                );

                if (youthAnswer && isWithinFilters(youthAnswer)) {
                    const path = `${youthAnswer.planName}.${youthAnswer.timepoint}.${domain}.${youthAnswer.pawnId}`;
                    accumulateAnswer(
                        scoreAverages,
                        path,
                        youthAnswer.answerValue,
                        currentYouth,
                        'youth',
                        youthAnswer.responseDate
                    );
                }

                currentYouth.parentRoles.caregiverList.forEach(caregiver => {
                    const caregiverAnswer = _get(
                        variableData.answers,
                        `${caregiver.pawn.pawnId}_${currentYouth.pawn.pawnId}`,
                        false
                    );

                    if (!caregiverAnswer) {
                        return;
                    }

                    // do not process this answer if it is not from this caregiver's youth's plan deployment
                    if (!_some(youthParentPlanDeploymentIds, id => id === caregiverAnswer.parentPlanDeploymentId)) {
                        return;
                    }

                    if (isWithinFilters(caregiverAnswer)) {
                        const path = `${caregiverAnswer.planName}.${caregiverAnswer.timepoint}.${domain}.${caregiver.pawn.pawnId}_${currentYouth.pawn.pawnId}`;
                        accumulateAnswer(
                            scoreAverages,
                            path,
                            caregiverAnswer.answerValue,
                            caregiver,
                            'caregiver',
                            caregiverAnswer.responseDate
                        );
                    }
                });
            });
        });
    });

    return scoreAverages;
};

export const queryAllAnswerData = createAsyncThunk('dashboard/queryAllAnswerData', async () => {
    const queryResult = await client.query({
        query: isSupervisor ? getAnswersSupervisorQuery : getAnswersQuery,
        fetchPolicy: 'network-only',
        variables: isSupervisor
            ? {
                  assignmentIds: ORG_CUSTOM_PLAN_ASSIGNMENT_HANDLES.onboarding.assignmentIds,
                  assignmentHandles: ORG_CUSTOM_PLAN_ASSIGNMENT_HANDLES.onboarding.handles,
              }
            : {
                  assignmentIds: ORG_CUSTOM_PLAN_ASSIGNMENT_HANDLES.onboarding.assignmentIds,
                  assignmentHandles: ORG_CUSTOM_PLAN_ASSIGNMENT_HANDLES.onboarding.handles,
                  careCoordinatorPawnId: USER.id,
              },
    });

    const data = _get(queryResult, 'data', {});

    // separate query result data into domain categories
    const res = {};
    let alliance = {};
    let fidelity = {};
    let satisfaction = {};
    let progress = {};
    let outcomes = {};
    let outcomesCaregiver = {};
    let feedback = {};
    data.metrics.variableList.forEach(variable => {
        let relevantVariableInfo = {};

        // move data around so it is cleaner when adding to tables
        relevantVariableInfo.assignmentHandle = _get(
            variable,
            'key.assignment.deploymentList[0].assignment.assignmentHandle',
            ''
        );
        relevantVariableInfo.planWeek = variable.survey.label;
        relevantVariableInfo.question = variable.key.title;
        relevantVariableInfo.variableKey = variable.reportHeader;
        relevantVariableInfo.planName = relevantVariableInfo.assignmentHandle.includes('_')
            ? relevantVariableInfo.assignmentHandle.split('_')[0]
            : '';
        relevantVariableInfo.answerOptions = variable.key.enumList;
        relevantVariableInfo.questionType = variable.key.questionType;
        relevantVariableInfo.week = variable.survey.label.split('Week')[1].trim();
        relevantVariableInfo.variableId = variable.key.keyId;

        let isAlliance = false;
        let isFidelity = false;
        let isSatisfaction = false;
        let isProgress = false;
        let isOutcomes = false;
        let isOutcomesCaregiver = false;
        let isFeedback = false;

        // separate into domains
        variable.tagList.forEach(tag => {
            switch (tag.internalName) {
                case 'Alliance':
                    relevantVariableInfo.domain = 'Alliance';
                    isAlliance = true;
                    break;
                case 'Fidelity':
                    relevantVariableInfo.domain = 'Fidelity';
                    isFidelity = true;
                    break;
                case 'Satisfaction':
                    relevantVariableInfo.domain = 'Satisfaction';
                    isSatisfaction = true;
                    break;
                case 'Progress':
                    relevantVariableInfo.domain = 'Progress';
                    isProgress = true;
                    break;
                case 'Outcomes':
                    relevantVariableInfo.domain = 'Outcomes';
                    isOutcomes = true;
                    break;
                case 'OutcomesCaregiver':
                    relevantVariableInfo.domain = 'OutcomesCaregiver';
                    isOutcomesCaregiver = true;
                    break;
                case 'Feedback':
                    relevantVariableInfo.domain = 'Feedback';
                    isFeedback = true;
                    break;
                default:
                    break;
            }
        });

        // push to corresponding domain
        if (isAlliance) {
            alliance[relevantVariableInfo.variableKey] = relevantVariableInfo;
        } else if (isFidelity) {
            fidelity[relevantVariableInfo.variableKey] = relevantVariableInfo;
        } else if (isSatisfaction) {
            satisfaction[relevantVariableInfo.variableKey] = relevantVariableInfo;
        } else if (isProgress) {
            progress[relevantVariableInfo.variableKey] = relevantVariableInfo;
        } else if (isOutcomes) {
            outcomes[relevantVariableInfo.variableKey] = relevantVariableInfo;
        } else if (isOutcomesCaregiver) {
            outcomesCaregiver[relevantVariableInfo.variableKey] = relevantVariableInfo;
        } else if (isFeedback) {
            feedback[relevantVariableInfo.variableKey] = relevantVariableInfo;
        }
    });

    // assign domain data to result
    res.alliance = alliance;
    res.fidelity = fidelity;
    res.satisfaction = satisfaction;
    res.progress = progress;
    res.outcomes = outcomes;
    res.outcomesCaregiver = outcomesCaregiver;
    res.feedback = feedback;

    // cross reference with answer results
    data.roles.youthList.forEach(youth => {
        const youthParentPlanDeploymentIds = _get(youth, 'selectedAssignmentPlanList', []).map(
            assignmentPlan => assignmentPlan.assignmentPlanId
        );

        // get answers from caregivers first
        _get(youth, 'parentRoles.caregiverList', []).forEach(caregiver => {
            const caregiverPawnId = caregiver.pawn.pawnId;
            const role = caregiver.role.label;
            const fields = caregiver.fields;

            caregiver.assignmentProgressList.forEach(caregiverProgress => {
                const timepoint = caregiverProgress.deployment.timepoint;
                const assignmentId = caregiverProgress.deployment.assignment.assignmentId;
                const deploymentId = caregiverProgress.deployment.deploymentId;
                const caregiverProgressParentPlanDeploymentId = caregiverProgress.session.assignmentPlanId;

                // do not process this progress if it does not belong to the deployments
                // under this caregiver's youth
                if (!_some(youthParentPlanDeploymentIds, id => id === caregiverProgressParentPlanDeploymentId)) {
                    return;
                }

                Object.keys(caregiverProgress.variables).forEach(variableKey => {
                    const value = caregiverProgress.variables[variableKey];
                    let answerText = '';

                    // find variableKey in res[domain]
                    Object.keys(res).forEach(domainName => {
                        if (variableKey === 'assignment' + assignmentId + '_end') {
                            let responseDate = value;
                            if (!res[domainName].answerResponseDates) {
                                res[domainName].answerResponseDates = [];
                            }
                            res[domainName].answerResponseDates.push({
                                pawnId: caregiverPawnId,
                                responseDate,
                                timepoint,
                                deploymentId,
                            });
                        } else if (Object.keys(res[domainName]).includes(variableKey)) {
                            if (!res[domainName][variableKey].answers) {
                                res[domainName][variableKey].answers = {}; // by pawnId key
                            }

                            let responseDate = _get(res, [domainName, 'answerResponseDates'], []).find(dateInfo => {
                                if (
                                    dateInfo.pawnId === caregiverPawnId &&
                                    dateInfo.timepoint === timepoint &&
                                    dateInfo.deploymentId === deploymentId
                                ) {
                                    return true;
                                }
                                return false;
                            });
                            if (responseDate) {
                                responseDate = responseDate.responseDate;
                            }

                            let answerValue = '';

                            if (res[domainName][variableKey].questionType === 'openEnded') {
                                answerValue = value;
                                answerText = value;
                            } else {
                                answerText = caregiverProgress.variablesRaw[variableKey] ?? '';
                                answerValue = Number(value);
                            }

                            res[domainName][variableKey].answers[`${caregiverPawnId}_${youth.pawn.pawnId}`] = {
                                answer: answerText,
                                answerValue,
                                timepoint,
                                role,
                                fields,
                                pawnId: caregiverPawnId,
                                variableKey,
                                youth: youth.pawn.pawnId,
                                question: res[domainName][variableKey].question,
                                planName: res[domainName][variableKey].planName,
                                planWeek: res[domainName][variableKey].planWeek,
                                responseDate: responseDate ? responseDate : null,
                                parentPlanDeploymentId: caregiverProgressParentPlanDeploymentId,
                            };
                        }
                    });
                });
            });
        });

        // get answers from youth
        const pawnId = youth.pawn.pawnId;
        const role = youth.role.label;
        const fields = youth.fields;
        youth.assignmentProgressList.forEach(youthAssignmentProgress => {
            const timepoint = youthAssignmentProgress.deployment.timepoint;
            const assignmentId = youthAssignmentProgress.deployment.assignment.assignmentId;
            const deploymentId = youthAssignmentProgress.deployment.deploymentId;
            const youthProgressParentPlanDeploymentId = youthAssignmentProgress.session.assignmentPlanId;

            Object.keys(youthAssignmentProgress.variables).forEach(variableKey => {
                const value = youthAssignmentProgress.variables[variableKey];
                let answerText = '';

                // find variableKey in res[domain]
                Object.keys(res).forEach(domainName => {
                    if (variableKey === 'assignment' + assignmentId + '_end') {
                        let responseDate = value;
                        if (!res[domainName].answerResponseDates) {
                            res[domainName].answerResponseDates = [];
                        }
                        res[domainName].answerResponseDates.push({
                            pawnId,
                            responseDate,
                            timepoint,
                            deploymentId,
                        });
                    } else if (Object.keys(res[domainName]).includes(variableKey)) {
                        if (!res[domainName][variableKey].answers) {
                            res[domainName][variableKey].answers = {}; // by pawnId key
                        }

                        let responseDate = _get(res, [domainName, 'answerResponseDates'], []).find(dateInfo => {
                            if (
                                dateInfo.pawnId === pawnId &&
                                dateInfo.timepoint === timepoint &&
                                dateInfo.deploymentId === deploymentId
                            ) {
                                return true;
                            }
                            return false;
                        });
                        if (responseDate) {
                            responseDate = responseDate.responseDate;
                        }

                        let answerValue = '';

                        if (res[domainName][variableKey].questionType === 'openEnded') {
                            answerValue = value;
                            answerText = value;
                        } else {
                            answerText = youthAssignmentProgress.variablesRaw[variableKey] ?? '';
                            answerValue = Number(value);
                        }

                        res[domainName][variableKey].answers[pawnId] = {
                            answer: answerText,
                            answerValue,
                            timepoint,
                            role,
                            fields,
                            pawnId,
                            variableKey,
                            question: res[domainName][variableKey].question,
                            caregivers: [
                                ..._get(youth, 'parentRoles.caregiverList', []).map(caregiver => caregiver.pawn.pawnId),
                            ],
                            planName: res[domainName][variableKey].planName,
                            planWeek: res[domainName][variableKey].planWeek,
                            responseDate: responseDate ? responseDate : null,
                            parentPlanDeploymentId: youthProgressParentPlanDeploymentId,
                        };
                    }
                });
            });
        });
    });

    return res;
});

export const queryAdministeredPlansData = createAsyncThunk('dashboard/queryAdministeredPlansData', async () => {
    const response = await client.query({
        query: isSupervisor ? getAdministeredPlansSupervisorQuery : getAdministeredPlansQuery,
        fetchPolicy: 'network-only',
        ...(isSupervisor ? {} : { variables: { careCoordinatorPawnId: USER.id } }),
    });

    let administeredPlansData = {};
    let youthAndCaregiverSessions = [];

    _get(response, 'data.roles.youthList', []).forEach(youth => {
        let pawnId = youth.pawn.pawnId;
        let plans = youth.selectedAssignmentPlanList;
        const sessionProgress = youth.assignmentProgressList;
        const caregiverSessionProgress = youth.parentRoles.caregiverList.flatMap(
            caregiver => caregiver.assignmentProgressList
        );

        administeredPlansData[pawnId] = plans.map(plan => {
            const originalStartDate = JSON.parse(plan.settings).startDate;
            const isStopped = _get(JSON.parse(plan.settings), 'stopPlan', false);
            const hasNotOpenedYet = !isDateInPast(originalStartDate);

            const isClosed =
                plan.sessionList.filter(session => session.deployment.isOpen || !isDateInPast(session.opensDate))
                    .length === 0;

            // add info for if each survey has been opened / completed
            const sessionList = plan.sessionList.map(assignedSession => {
                const assignedSessionId = assignedSession.sessionDeploymentInfo.deploymentId;
                const assignedSessionPawnId = assignedSession.sessionDeploymentInfo.pawnId;

                let openedSession = sessionProgress.find(
                    session =>
                        session.deployment.deploymentId === assignedSessionId &&
                        session.pawnId === assignedSessionPawnId
                );
                // look in caregivers
                if (!openedSession) {
                    openedSession = caregiverSessionProgress.find(
                        session =>
                            session.deployment.deploymentId === assignedSessionId &&
                            session.pawnId === assignedSessionPawnId
                    );
                }

                return {
                    ...assignedSession,
                    startedDate: openedSession ? openedSession.startedDate : '',
                };
            });

            return {
                ...plan,
                isStopped,
                hasNotOpenedYet,
                isClosed,
                sessionList,
                wrapIds: [
                    { pawnId: youth.pawn.pawnId, wrapId: youth.fields.wrapId },
                    ...youth.parentRoles.caregiverList.map(caregiver => ({
                        pawnId: caregiver.pawn.pawnId,
                        wrapId: caregiver.fields.wrapId,
                    })),
                ],
            };
        });
    });

    _get(response, 'data.roles.youthList', []).forEach(youth => {
        youthAndCaregiverSessions.push(
            ..._get(administeredPlansData, youth.pawn.pawnId, []).flatMap(plan => {
                const settings = JSON.parse(plan.settings);
                const planName = settings.planName;
                const startDate = settings.startDate;
                const completedDate = plan.completedDate;
                const planDeploymentId = plan.assignmentPlanId;

                const sessionsByIdMap = {};

                plan.sessionList.forEach(session => {
                    const pawnId = session.sessionDeploymentInfo.pawnId;

                    if (!sessionsByIdMap[pawnId]) {
                        sessionsByIdMap[pawnId] = [];
                    }

                    sessionsByIdMap[pawnId].push({
                        ...session,
                        role: session.deployment.role,
                        completedDate,
                        planDeploymentId,
                    });
                });

                const sessionsById = Object.entries(sessionsByIdMap).map(([pawnId, sessions]) => {
                    return {
                        pawnId: Number(pawnId),
                        sessions,
                        role: sessions[0].role,
                        planName,
                        startDate,
                        wrapId: plan.wrapIds.find(wrapIdInfo => {
                            return wrapIdInfo.pawnId === sessions[0].sessionDeploymentInfo.pawnId;
                        }).wrapId,
                        completedDate,
                        sessionMessagesSent: sessions.filter(
                            session => session.opensDaysAgo > 0 && _get(session, 'settings.messageScheduled', false)
                        ).length,

                        sessionsCompleted: sessions.filter(session => session.completed).length,
                        sessionsStarted: sessions.filter(session => session.startedDate).length,
                        planDeploymentId,
                    };
                });

                return sessionsById;
            })
        );
    });

    return { administeredPlansData, youthAndCaregiverSessions };
});

const dashboardSlice = createSlice({
    name: 'dashboard',
    initialState,
    reducers: {
        setSelectedYouth: (state, action) => {
            state.selectedYouth = action.payload;
        },
        // Stores the result of computeScoreAverages; dispatched by Dashboard/index.js on filter/data change
        setAggregateScoreAverages: (state, action) => {
            state.aggregateScoreAverages = action.payload;
        },
        setStartDateFilter: (state, action) => {
            state.filters.startDate = action.payload;
        },
        setEndDateFilter: (state, action) => {
            state.filters.endDate = action.payload;
        },
        setPlanNameFilter: (state, action) => {
            state.filters.planName = action.payload;
        },
        setAdministeredPlansData: (state, action) => {
            state.administeredPlansData = action.payload;
        },
        setSelectedCareCoordinator: (state, action) => {
            state.selectedCareCoordinator = action.payload;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(queryAllAnswerData.pending, () => {})
            .addCase(queryAllAnswerData.fulfilled, (state, action) => {
                state.allAnswerData = action.payload;
            })
            .addCase(queryAdministeredPlansData.pending, () => {})
            .addCase(queryAdministeredPlansData.fulfilled, (state, action) => {
                state.administeredPlansData = action.payload.administeredPlansData;
                state.youthAndCaregiverSessions = action.payload.youthAndCaregiverSessions;
            });
    },
});

export const {
    setSelectedYouth,
    setSelectedCareCoordinator,
    setAggregateScoreAverages,
    setStartDateFilter,
    setEndDateFilter,
    setPlanNameFilter,
    setAdministeredPlansData,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;
